import { AppRouteRecordRaw } from '../types';

const Body = () => import('@/components/body.vue');
const ApiTester = () =>
  import('@/pages/internal-developer-hub/developer-tools/api-tester/ApiTester.vue');
const ApiDocUpdater = () =>
  import('@/pages/internal-developer-hub/developer-tools/api-doc-updater/ApiDocUpdater.vue');
const RydPortalApisDocumentation = () =>
  import('@/pages/internal-developer-hub/developer-tools/ryd-portal-api-documentation/RydPortalApisDocumentation.vue');
const FeedbackAttachmentsGuide = () =>
  import('@/pages/internal-developer-hub/developer-tools/feedback-attachments-guide/FeedbackAttachmentsGuide.vue');

// Automated Tests Pages
const AutomatedTestsOverview = () =>
  import('@/pages/internal-developer-hub/automated-tests/overview/AutomatedTestsOverview.vue');
const ConnectTestAutomation = () =>
  import('@/pages/internal-developer-hub/automated-tests/connect-test-automation/ConnectTestAutomation.vue');
const ConnectTestAutomationReport = () =>
  import('@/pages/internal-developer-hub/automated-tests/connect-test-automation/ConnectTestAutomationReport.vue');
const ApiTestAutomation = () =>
  import('@/pages/internal-developer-hub/automated-tests/api-test-automation/ApiTestAutomation.vue');
const WebappTestAutomation = () =>
  import('@/pages/internal-developer-hub/automated-tests/webapp-test-automation/WebappTestAutomation.vue');
const RydPortalTestAutomation = () =>
  import('@/pages/internal-developer-hub/automated-tests/ryd-portal/RydPortalTestAutomation.vue');
const OdooTestAutomation = () =>
  import('@/pages/internal-developer-hub/automated-tests/odoo-test-automation/OdooTestAutomation.vue');
const AutomatedTestsTriggerBuilder = () =>
  import('@/pages/internal-developer-hub/automated-tests/trigger-builder/AutomatedTestsTriggerBuilder.vue');

// Usage of the backends per company, user and AI agent (ticket 2026-10-18-usage-limits-and-traffic)
const UsageOverview = () =>
  import('@/pages/internal-developer-hub/usage-overview/UsageOverview.vue');

// API Documentation (topic 2026-10-openapi-documentation) — replaces the Services landing page
const ApiDocsCatalog = () =>
  import('@/pages/internal-developer-hub/api-docs/catalog/ApiDocsCatalog.vue');
const ApiDocsViewer = () =>
  import('@/pages/internal-developer-hub/api-docs/viewer/ApiDocsViewer.vue');
const ApiDocsManage = () =>
  import('@/pages/internal-developer-hub/api-docs/manage/ApiDocsManage.vue');
const ApiDocsAuditLog = () =>
  import('@/pages/internal-developer-hub/api-docs/audit-log/ApiDocsAuditLog.vue');
const HighMobilitySwaggerPage = () =>
  import('@/pages/internal-developer-hub/services/HighMobilitySwaggerPage.vue');
const OrderServiceSwaggerPage = () =>
  import('@/pages/internal-developer-hub/services/OrderServiceSwaggerPage.vue');

// odoo
const OdooGettingStarted = () =>
  import('@/pages/internal-developer-hub/odoo/getting-started/OdooGettingStarted.vue');
const OdooApiReference = () =>
  import('@/pages/internal-developer-hub/odoo/rest-api/api-reference/OdooApiReference.vue');

// REST API Pages
const KeyConcepts = () =>
  import('@/pages/internal-developer-hub/odoo/key-concepts/KeyConcepts.vue');
const RestApiIntroduction = () =>
  import('@/pages/internal-developer-hub/odoo/rest-api/introduction/RestApiIntroduction.vue');
const TransitoryDataGuide = () =>
  import('@/pages/internal-developer-hub/odoo/rest-api/transitory-data/TransitoryDataGuide.vue');
const IdempotencyGuide = () =>
  import('@/pages/internal-developer-hub/odoo/rest-api/idempotency/IdempotencyGuide.vue');
const OrderLifecycle = () =>
  import('@/pages/internal-developer-hub/odoo/rest-api/order-lifecycle/OrderLifecycle.vue');

// XML-RPC API Pages
const XmlRpcIntroduction = () =>
  import('@/pages/internal-developer-hub/odoo/xmlrpc-api/introduction/XmlRpcIntroduction.vue');
const OdooModelExplorer = () =>
  import('@/pages/internal-developer-hub/odoo/xmlrpc-api/odoo-model-explorer/OdooModelExplorer.vue');

// Discount Management Pages
const DiscountManagementIntro = () =>
  import('@/pages/internal-developer-hub/discount-management/introduction/DiscountManagementIntro.vue');
const ListPriceGuide = () =>
  import('@/pages/internal-developer-hub/discount-management/list-price-guide/ListPriceGuide.vue');
const DiscountManagementApiReference = () =>
  import('@/pages/internal-developer-hub/discount-management/api-reference/ApiReference.vue');

// UI Settings API Pages
const UISettingsOverview = () =>
  import('@/pages/internal-developer-hub/ui-settings/overview/UISettingsOverview.vue');
const UISettingsIntegrationGuide = () =>
  import('@/pages/internal-developer-hub/ui-settings/integration-guide/UISettingsIntegrationGuide.vue');
const UISettingsSwaggerReference = () =>
  import('@/pages/internal-developer-hub/ui-settings/api-reference/UISettingsSwaggerReference.vue');
const InternalDeveloperHubOverview = () =>
  import('@/pages/internal-developer-hub/overview/InternalDeveloperHubOverview.vue');

export const developerHubRoutes: AppRouteRecordRaw[] = [
  {
    path: '/internal-developer-hub',
    component: Body,
    meta: {
      breadcrumb: 'Developer Hub',
    },
    children: [
      {
        path: 'overview',
        name: 'internalDeveloperHubOverview',
        component: InternalDeveloperHubOverview,
        meta: {
          title: 'Internal Developer Hub Overview | ryd Portal',
          breadcrumb: 'Overview',
        },
      },
      {
        path: 'usage-overview',
        name: 'usageOverview',
        component: UsageOverview,
        meta: {
          title: 'Usage Overview | ryd Portal',
          breadcrumb: 'Usage Overview',
          requiredUserGroup: ['ryd'],
        },
      },
      {
        path: 'api-docs',
        name: 'apiDocsCatalog',
        component: ApiDocsCatalog,
        meta: {
          title: 'API Documentation | ryd Portal',
          breadcrumb: 'API Documentation',
          requiredUserGroup: ['ryd'],
        },
      },
      {
        // Literal path before `api-docs/:serviceId` so it is never read as a service id.
        path: 'api-docs/manage',
        name: 'apiDocsManage',
        component: ApiDocsManage,
        meta: {
          title: 'Manage API Documentation | ryd Portal',
          breadcrumb: 'Manage Services',
          requiredUserGroup: ['ryd'],
          requiredRoles: ['api_docs_admin'],
        },
      },
      {
        path: 'api-docs/audit-log',
        name: 'apiDocsAuditLog',
        component: ApiDocsAuditLog,
        meta: {
          title: 'Try-out Audit Log | ryd Portal',
          breadcrumb: 'Try-out Audit Log',
          requiredUserGroup: ['ryd'],
          requiredRoles: ['api_docs_admin'],
        },
      },
      {
        path: 'api-docs/:serviceId',
        name: 'apiDocsViewer',
        component: ApiDocsViewer,
        meta: {
          title: 'API Documentation | ryd Portal',
          breadcrumb: 'Service',
          requiredUserGroup: ['ryd'],
        },
      },
      {
        // The Services landing page was replaced by the catalogue.
        path: 'services',
        name: 'developerHubServices',
        redirect: { name: 'apiDocsCatalog' },
      },
      {
        path: 'services/high-mobility',
        name: 'developerHubServiceHighMobility',
        component: HighMobilitySwaggerPage,
        meta: {
          title: 'High Mobility Swagger | ryd Portal',
          breadcrumb: 'High Mobility',
          requiredUserGroup: ['ryd'],
        },
      },
      {
        path: 'services/order-service',
        name: 'developerHubServiceOrderService',
        component: OrderServiceSwaggerPage,
        meta: {
          title: 'Order Service Swagger | ryd Portal',
          breadcrumb: 'Order Service',
          requiredUserGroup: ['ryd'],
        },
      },
      {
        // Migrated to the catalogue (D-12). The hash carries swagger-ui deep links
        // (e.g. Partner Pricing's `#/partner-pricing/<operationId>`), so it is kept.
        path: 'services/user-service',
        name: 'developerHubServiceUserService',
        redirect: (to) => ({
          name: 'apiDocsViewer',
          params: { serviceId: 'user-service' },
          hash: to.hash,
        }),
      },
      {
        path: 'developer-tools/api-tester',
        name: 'apiTester',
        component: ApiTester,
        meta: {
          title: 'API Tester',
          breadcrumb: 'API Tester',
        },
      },
      {
        path: 'developer-tools/api-doc-updater',
        name: 'apiDocUpdater',
        component: ApiDocUpdater,
        meta: {
          title: 'API Doc Updater',
          breadcrumb: 'API Documentation Updater',
        },
      },
      {
        path: 'developer-tools/ryd-portal-api-documentation',
        name: 'rydPortalApiDocumentation',
        component: RydPortalApisDocumentation,
        meta: {
          title: 'ryd Portal API Documentation',
          breadcrumb: 'Ryd Portal APIs Documentation',
        },
      },
      {
        path: 'developer-tools/feedback-attachments-guide',
        name: 'feedbackAttachmentsGuide',
        component: FeedbackAttachmentsGuide,
        meta: {
          title: 'Feedback & Attachments API Guide',
          breadcrumb: 'Feedback Comments & Attachments Guide',
        },
      },
      // Automated Tests Routes
      {
        path: 'automated-tests/overview',
        name: 'automatedTestsOverview',
        component: AutomatedTestsOverview,
        meta: {
          title: 'Automated Tests Overview | ryd Portal',
          breadcrumb: 'Automated Tests Overview',
        },
      },
      {
        path: 'automated-tests/trigger-builder',
        name: 'automatedTestsTriggerBuilder',
        component: AutomatedTestsTriggerBuilder,
        meta: {
          title: 'AI Prompts & Webhook Trigger Builder',
          breadcrumb: 'AI & Webhook Triggers',
        },
      },
      {
        path: 'automated-tests/connect-test-automation',
        name: 'connectTestAutomation',
        component: ConnectTestAutomation,
        meta: {
          title: 'Connect Test Automation',
          breadcrumb: 'Connect Test Automation Dashboard',
        },
      },
      {
        path: 'automated-tests/connect-test-automation/reports/:runId',
        name: 'connectTestAutomationReport',
        component: ConnectTestAutomationReport,
        meta: {
          title: 'Connect Test Automation Report',
          breadcrumb: 'Connect Test Report',
          requiredUserGroup: ['ryd'],
        },
      },
      {
        path: 'automated-tests/api-test-automation',
        name: 'apiTestAutomation',
        component: ApiTestAutomation,
        meta: {
          title: 'API Test Automation',
          breadcrumb: 'API Test Automation Dashboard',
        },
      },
      {
        path: 'automated-tests/webapp-test-automation',
        name: 'webappTestAutomation',
        component: WebappTestAutomation,
        meta: {
          title: 'Webapp Test Automation',
          breadcrumb: 'Webapp Test Automation Dashboard',
        },
      },
      {
        path: 'automated-tests/ryd-portal',
        name: 'rydPortalTestAutomation',
        component: RydPortalTestAutomation,
        meta: {
          title: 'ryd Portal Test Automation',
          breadcrumb: 'ryd Portal Test Automation Dashboard',
        },
      },
      {
        path: 'automated-tests/odoo-test-automation',
        name: 'odooTestAutomation',
        component: OdooTestAutomation,
        meta: {
          title: 'Odoo Test Automation',
          breadcrumb: 'Odoo Test Automation Dashboard',
        },
      },
      // Backwards Compatibility Redirects
      {
        path: 'developer-tools/connect-test-automation',
        redirect: '/internal-developer-hub/automated-tests/connect-test-automation',
      },
      {
        path: 'developer-tools/api-test-automation',
        redirect: '/internal-developer-hub/automated-tests/api-test-automation',
      },
      {
        path: 'odoo/getting-started',
        name: 'odooGettingStarted',
        component: OdooGettingStarted,
        meta: {
          title: 'Odoo Getting Started',
          breadcrumb: 'Odoo Integration - Getting Started',
        },
      },
      {
        path: 'odoo/key-concepts',
        name: 'odooKeyConcepts',
        component: KeyConcepts,
        meta: {
          title: 'Odoo - Key Concepts',
          breadcrumb: 'Odoo Integration - Key Concepts',
        },
      },
      // REST API Routes
      {
        path: 'odoo/rest-api/api-reference',
        name: 'odooApiReference',
        component: OdooApiReference,
        meta: {
          title: 'Odoo API Reference',
          breadcrumb: 'Odoo Integration API Reference',
        },
      },
      {
        path: 'odoo/rest-api/introduction',
        name: 'restApiIntroduction',
        component: RestApiIntroduction,
        meta: {
          title: 'REST API - Introduction',
          breadcrumb: 'REST API - Authentication & Usage',
        },
      },
      {
        path: 'odoo/rest-api/transitory-data',
        name: 'transitoryDataGuide',
        component: TransitoryDataGuide,
        meta: {
          title: 'REST API - Transitory Data',
          breadcrumb: 'REST API - Transitory Data Guide',
        },
      },
      {
        path: 'odoo/rest-api/idempotency',
        name: 'idempotencyGuide',
        component: IdempotencyGuide,
        meta: {
          title: 'REST API - Idempotency',
          breadcrumb: 'REST API - Idempotency Guide',
        },
      },
      {
        path: 'odoo/rest-api/order-lifecycle',
        name: 'orderLifecycle',
        component: OrderLifecycle,
        meta: {
          title: 'REST API - Order Lifecycle',
          breadcrumb: 'REST API - Order Lifecycle',
        },
      },
      // XML-RPC API Routes
      {
        path: 'odoo/xmlrpc-api/introduction',
        name: 'XmlRpcIntroduction',
        component: XmlRpcIntroduction,
        meta: {
          title: 'XML-RPC API - Introduction',
          breadcrumb: 'XML-RPC API - Getting Started',
        },
      },
      {
        path: 'odoo/xmlrpc-api/odoo-model-explorer',
        name: 'odooModelExplorer',
        component: OdooModelExplorer,
        meta: {
          title: 'Odoo Model Explorer',
          breadcrumb: 'Odoo Model Explorer',
        },
      },
      // Discount Management Routes
      {
        path: 'discount-management/introduction',
        name: 'discountManagementIntro',
        component: DiscountManagementIntro,
        meta: {
          title: 'Discount Management - Introduction',
          breadcrumb: 'Discount Management - Introduction',
        },
      },
      {
        path: 'discount-management/list-price-guide',
        name: 'listPriceGuide',
        component: ListPriceGuide,
        meta: {
          title: 'List Price Integration Guide',
          breadcrumb: 'List Price Integration Guide',
        },
      },
      {
        path: 'discount-management/api-reference',
        name: 'discountManagementApiReference',
        component: DiscountManagementApiReference,
        meta: {
          title: 'Discount Management - API Reference',
          breadcrumb: 'Discount Management API Reference',
        },
      },
      // UI Settings API Routes
      {
        path: 'ui-settings/overview',
        name: 'uiSettingsOverview',
        component: UISettingsOverview,
        meta: {
          title: 'UI Settings API - Overview',
          breadcrumb: 'UI Settings API - Overview',
        },
      },
      {
        path: 'ui-settings/integration-guide',
        name: 'uiSettingsIntegrationGuide',
        component: UISettingsIntegrationGuide,
        meta: {
          title: 'UI Settings API - Integration Guide',
          breadcrumb: 'UI Settings API - Integration Guide',
        },
      },
      {
        path: 'ui-settings/api-reference',
        name: 'uiSettingsApiReference',
        component: UISettingsSwaggerReference,
        meta: {
          title: 'UI Settings API - API Reference',
          breadcrumb: 'UI Settings API - API Reference',
        },
      },
    ],
  },
  {
    path: '/developer-hub/services',
    redirect: '/internal-developer-hub/api-docs',
  },
  {
    path: '/developer-hub/services/high-mobility',
    redirect: '/internal-developer-hub/services/high-mobility',
  },
];
